import { useEffect, useRef, useState, Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { Studio } from "./studio";
import ServiceModel from "./ServiceModel";

function useCapabilities() {
  const [caps, setCaps] = useState({ webgl: false, reduced: true });
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const webgl = !!(window.WebGLRenderingContext && c.getContext("webgl"));
      setCaps({ webgl, reduced });
    } catch {
      setCaps({ webgl: false, reduced: true });
    }
  }, []);
  return caps;
}

// Mounts the 3D canvas only when the card is near the viewport (perf),
// unmounts when far away to keep active WebGL contexts low.
export default function ServiceCanvas({ type, fallback }) {
  const wrapRef = useRef(null);
  const pointer = useRef({ x: 0, y: 0 });
  const [inView, setInView] = useState(false);
  const { webgl, reduced } = useCapabilities();

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "150px 0px" }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    pointer.current.x = ((e.clientX - r.left) / r.width) * 2 - 1;
    pointer.current.y = ((e.clientY - r.top) / r.height) * 2 - 1;
  };

  const active = webgl && !reduced;

  return (
    <div ref={wrapRef} onPointerMove={onMove} className="relative h-full w-full">
      {active && inView ? (
        <Canvas camera={{ position: [0, 0, 4], fov: 45 }} dpr={[1, 1.5]} gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}>
          <Studio />
          <Suspense fallback={null}>
            <ServiceModel type={type} pointer={pointer} />
          </Suspense>
        </Canvas>
      ) : (
        <div className="flex h-full w-full items-center justify-center">{fallback}</div>
      )}
    </div>
  );
}
